import { CacheProvider } from "@emotion/react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createContext, useCallback, useMemo, useState } from "react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";
import IChildrenProps from "models/common-props/IChildrenProps";
import i18n from "localization/i18n";
import useIsSmUp from "hooks/useIsSmUp";
import { createAmeenTheme } from "theme/themeConfig";
import type { ExtendedMuiTheme } from "theme/themes/types";

export type PaletteMode = "light" | "dark";

const isDarkPrefered =
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const defaultMode: PaletteMode = isDarkPrefered ? "dark" : "light";

const ThemeContext = createContext({
  mode: defaultMode,
  setColorMode: (s: PaletteMode) => {},
});

export function ThemeContextProvider({ children }: IChildrenProps) {
  const isSmUp = useIsSmUp();
  const localMode = localStorage.getItem("localMode") as PaletteMode;
  let currentMode = defaultMode;

  if (localMode) {
    currentMode = localMode;
  }
  const [mode, setMode] = useState<PaletteMode>(currentMode);

  const isRtl = i18n.language === "ar";
  const isAr = i18n.language === "ar";

  document.dir = isRtl ? "rtl" : document.dir;

  const cacheRtl = useMemo(
    () =>
      createCache({
        key: "muirtl",
        stylisPlugins: isRtl ? [prefixer, rtlPlugin] : [],
      }),
    [isRtl]
  );


  const theme = useMemo<ExtendedMuiTheme>(
    () =>
      createAmeenTheme({
        mode: mode,
        direction: isRtl ? "rtl" : "ltr",
        language: isAr ? "ar" : "en",
      }) as ExtendedMuiTheme,
    [mode, isRtl, isAr]
  );

  // Theme is now complete with all variants from themeConfig
  // Typography variants are defined in src/theme/components/typography.theme.ts

  const setColorMode = useCallback((s: "light" | "dark") => {
    localStorage.setItem("localMode", s);
    setMode(s);
  
    // window.location.reload();
  }, []);

  const context = useMemo(() => ({ mode, setColorMode }), [mode, setColorMode]);

  return (
    <ThemeContext.Provider value={context}>
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            {children}
          </LocalizationProvider>
        </ThemeProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
